import React, { useState, useEffect } from 'react';
import { resourcesAPI } from './api';

export const AdvancedSearch = ({ onResults, initialQuery = '' }) => {
  const [query, setQuery] = useState(initialQuery);
  const [filters, setFilters] = useState({
    resourceType: '',
    category: '',
    author: '',
    dateRange: '',
    tags: [],
    rating: ''
  });
  const [availableFilters, setAvailableFilters] = useState({
    categories: [],
    authors: [],
    tags: []
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      // Get all resources to extract filter options
      const response = await resourcesAPI.getAll(null, null, null, 100);
      const resources = response.data;
      
      const categories = [...new Set(resources.map(r => r.category))].filter(Boolean);
      const authors = [...new Set(resources.map(r => r.author))].filter(Boolean);
      const allTags = resources.flatMap(r => r.tags || []);
      const tags = [...new Set(allTags)].filter(Boolean);
      
      setAvailableFilters({ categories, authors, tags });
    } catch {
      setAvailableFilters({ categories: [], authors: [], tags: [] });
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const searchParams = {
        search: query || null,
        resourceType: filters.resourceType || null,
        category: filters.category || null,
        limit: 50
      };
      
      const response = await resourcesAPI.getAll(
        searchParams.resourceType,
        searchParams.category,
        searchParams.search,
        searchParams.limit
      );
      
      // Apply additional client-side filters
      let filteredResults = response.data;
      
      if (filters.author) {
        filteredResults = filteredResults.filter(r => 
          r.author && r.author.toLowerCase().includes(filters.author.toLowerCase())
        );
      }
      
      if (filters.rating) {
        const minRating = parseFloat(filters.rating);
        filteredResults = filteredResults.filter(r => r.rating >= minRating);
      }
      
      if (filters.tags.length > 0) {
        filteredResults = filteredResults.filter(r => 
          r.tags && r.tags.some(tag => 
            filters.tags.some(filterTag => 
              tag.toLowerCase().includes(filterTag.toLowerCase())
            )
          )
        );
      }
      
      if (filters.dateRange) {
        const now = new Date();
        let cutoffDate;
        
        switch (filters.dateRange) {
          case 'week':
            cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case 'month':
            cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'year':
            cutoffDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
            break;
          default:
            cutoffDate = null;
        }
        
        if (cutoffDate) {
          filteredResults = filteredResults.filter(r => 
            r.date_published && new Date(r.date_published) >= cutoffDate
          );
        }
      }
      
      onResults(filteredResults, { query, ...filters });
    } catch {
      onResults([], { query, ...filters });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const handleTagToggle = (tag) => {
    setFilters(prev => ({
      ...prev,
      tags: prev.tags.includes(tag) 
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const clearFilters = () => {
    setFilters({
      resourceType: '',
      category: '',
      author: '',
      dateRange: '',
      tags: [],
      rating: ''
    });
    setQuery('');
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => 
      value && (Array.isArray(value) ? value.length > 0 : true)
    ).length + (query ? 1 : 0);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      {/* Basic Search */}
      <div className="gpj-advanced-search__bar flex space-x-4 mb-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search for legal resources, cases, guides..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          type="button"
          onClick={handleSearch}
          disabled={loading}
          className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition-colors disabled:opacity-50 flex items-center"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="border border-gray-300 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
          </svg>
          Filters
          {getActiveFilterCount() > 0 && (
            <span className="ml-2 bg-blue-600 text-white text-xs rounded-full px-2 py-1">
              {getActiveFilterCount()}
            </span>
          )}
        </button>
      </div>

      {/* Advanced Filters */}
      {isExpanded && (
        <div className="border-t border-gray-200 pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Resource Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resource Type
              </label>
              <select
                value={filters.resourceType}
                onChange={(e) => handleFilterChange('resourceType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="case_law">Case Law</option>
                <option value="legal_guide">Legal Guides</option>
                <option value="form_template">Form Templates</option>
                <option value="video_tutorial">Video Tutorials</option>
                <option value="article">Articles</option>
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Categories</option>
                {availableFilters.categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Rating
              </label>
              <select
                value={filters.rating}
                onChange={(e) => handleFilterChange('rating', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Any Rating</option>
                <option value="4">4+ Stars</option>
                <option value="3">3+ Stars</option>
                <option value="2">2+ Stars</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Author */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Author
              </label>
              <input
                type="text"
                placeholder="Search by author..."
                value={filters.author}
                onChange={(e) => handleFilterChange('author', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Published
              </label>
              <select
                value={filters.dateRange}
                onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Any Time</option>
                <option value="week">Past Week</option>
                <option value="month">Past Month</option>
                <option value="year">Past Year</option>
              </select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
              {availableFilters.tags.map(tag => (
                <button
                  type="button"
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filters.tags.includes(tag)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            {filters.tags.length > 0 && (
              <p className="text-sm text-gray-600 mt-2">
                Selected: {filters.tags.join(', ')}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="gpj-advanced-search__actions flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={clearFilters}
              className="text-gray-600 hover:text-gray-800 text-sm"
            >
              Clear All Filters
            </button>
            <button
              type="button"
              onClick={handleSearch}
              disabled={loading}
              className="bg-orange-500 text-white px-6 py-2 rounded hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
